import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { menuData } from '../data/menuData';
import colors from '../styles/colors';
import { useMultiLanguage } from '../hooks/useMultiLanguage';
import logo from '../assets/images/logo.png';

const PrintStyles = createGlobalStyle`
  @page { 
    size: A4; 
    margin: 12mm;
  }
  @media print {
    body { 
      -webkit-print-color-adjust: exact; 
      print-color-adjust: exact;
    }
    .no-print { display: none !important; }
    .page-break { page-break-after: always; }
    
    * {
      orphans: 3;
      widows: 3;
    }
    
    section { 
      border: none !important; 
    }
    
    h2 {
      page-break-after: avoid;
    }
  }
`;

const Wrapper = styled.div`
  max-width: 100%;
  margin: 0 auto;
  color: ${colors.text.primary};
  font-family: Arial, Helvetica, sans-serif;
  
  @media print {
    font-size: 11pt;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  
  @media print {
    margin-bottom: 8px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  margin: 0;
  
  @media print {
    font-size: 20px;
  }
`;

const Logo = styled.img`
  height: 40px;
  
  @media print {
    height: 32px;
  }
`;

const PrintButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #ccc;
  background: #fff;
  cursor: pointer;
`;

const Section = styled.section`
  margin: 14px 0;
  border: 2px solid ${colors.primary.green}40;
  border-radius: 8px;
  padding: 12px;
  
  @media print {
    margin: 4px 0;
    padding: 6px;
    page-break-inside: avoid;
    orphans: 4;
    widows: 4;
  }
`;

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 16px;
  
  @media print {
    gap: 3px 8px;
    page-break-inside: auto;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  margin: 0 0 6px;
  border-bottom: 2px solid ${colors.primary.green};
  padding-bottom: 4px;
  color: ${colors.primary.green};
  
  @media print {
    font-size: 18px;
    margin: 0 0 3px;
    padding-bottom: 2px;
  }
`;

const SectionSubtitle = styled.p`
  margin: 0 0 8px;
  color: #555;
  font-size: 12px;
  
  @media print {
    margin: 0 0 4px;
    font-size: 10px;
  }
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 6px 0;
  border-bottom: 1px dotted #ddd;
  break-inside: avoid;
  
  @media print {
    padding: 2px 0;
    page-break-inside: avoid;
    orphans: 2;
    widows: 2;
  }
`;

const ItemName = styled.div`
  font-weight: 700;
  font-size: 16px;
  
  @media print {
    font-size: 14px;
  }
`;

const ItemLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemDesc = styled.div`
  color: ${colors.text.secondary};
  font-size: 14px;
  line-height: 1.4;
  
  @media print {
    font-size: 12px;
  }
`;
const ItemDescSecondary = styled.div`
  color: ${colors.text.secondary}B3;
  font-size: 11px;
  font-style: italic;
  line-height: 1.3;
  
  @media print {
    font-size: 10px;
  }
`;

const ItemNote = styled.div`
  color: ${colors.primary.blue};
  font-size: 12px;
  font-style: italic;
  margin-top: 3px;
  font-weight: 500;
  
  @media print {
    font-size: 11px;
  }
`;

const ItemPrice = styled.div`
  white-space: nowrap;
  font-weight: 700;
  font-size: 17px;
  color: ${colors.primary.green};
  
  @media print {
    font-size: 15px;
  }
`;

function translateItem(t, id, fallbackName, fallbackDesc) {
  const base = `menu.items.${id}`;
  const name = t(`${base}.name`, { defaultValue: fallbackName });
  const description = t(`${base}.description`, { defaultValue: fallbackDesc || '' });
  return { name, description };
}

const SectionNote = styled.div`
  text-align: center;
  margin-top: 8px;
  padding: 6px;
  background: ${colors.primary.green}10;
  border-radius: 6px;
  border-left: 2px solid ${colors.primary.green};
  font-size: 11px;
  font-weight: 500;
  color: ${colors.text.primary};
  
  @media print {
    margin-top: 4px;
    padding: 4px;
    font-size: 10px;
  }
`;

const DessertsSection = styled(Section)`
  @media print {
    margin-top: 2px;
    page-break-inside: avoid;
    page-break-after: auto;
    orphans: 2;
    widows: 2;
  }
`;

const Block = ({ t, keyName, getTranslations }) => {
  const block = menuData[keyName];
  if (!block) return null;
  
  // Função para traduzir a nota
  const translateNote = (note) => {
    if (!note) return note;
    
    // Mapear padrões de notas para chaves de tradução
    if (note.includes('Farofa / Vinagrete / Salada') && note.includes('Farofa como primeiro')) {
      return t('menu.sidesFarofa');
    } else if (note.includes('Salada / Vinagrete / Salsa Rose')) {
      return t('menu.sidesSalada');
    } else if (note.includes('Vinagrete / Farofa / Salada')) {
      return t('menu.sidesVinagrete');
    } else if (note.includes('Salada / Vinagrete / Farofa')) {
      return t('menu.sidesReverse');
    } else if (note.includes('Salsa Rose / Vinagrete / Salada')) {
      return t('menu.sidesSaladaVinagrete');
    }
    // Se não corresponde a nenhum padrão conhecido, retornar original
    return note.replace('Acompanhamento:', t('menu.sides'));
  };
  
  // Função para traduzir unidades
  const translateUnit = (unit) => {
    if (!unit) return unit;
    
    // Substituir "cada" por tradução
    if (unit === 'cada') {
      return t('menu.unit.each');
    }
    
    // Substituir "unidades" ou "unidade" por tradução
    if (unit.includes('unidades')) {
      return unit.replace('unidades', t('menu.unit.units'));
    }
    if (unit.includes('unidade')) {
      return unit.replace('unidade', t('menu.unit.units'));
    }
    
    return unit;
  };
  
  // Usar DessertsSection para sobremesas para permitir melhor compartilhamento de página
  const SectionComponent = keyName === 'sobremesas' ? DessertsSection : Section;
  
  return (
    <SectionComponent>
      <SectionTitle>{t(`menu.${keyName}`, { defaultValue: block.title })}</SectionTitle>
      {block.subtitle ? (
        <SectionSubtitle>{t(`menu.${keyName}Subtitle`, { defaultValue: block.subtitle })}</SectionSubtitle>
      ) : null}
      <ItemGrid>
        {block.items?.map((it) => {
          const name = getTranslations(it.id, 'name');
          const desc = getTranslations(it.id, 'description');
          return (
            <ItemRow key={`${keyName}-${it.id}`}>
              <ItemLeft>
                <ItemName>{name.mainText || it.name}</ItemName>
                {(desc.mainText || it.description) ? <ItemDesc>{desc.mainText || it.description}</ItemDesc> : null}
                {desc.secondaryTexts.map((s, idx) => (
                  <ItemDescSecondary key={idx}>{s.text}</ItemDescSecondary>
                ))}
                {it.note && <ItemNote>{translateNote(it.note)}</ItemNote>}
                {it.unit && <ItemNote>{translateUnit(it.unit)}</ItemNote>}
              </ItemLeft>
              <ItemPrice>{it.price}</ItemPrice>
            </ItemRow>
          );
        })}
      </ItemGrid>
      {block.note && (
        <SectionNote>
          {keyName === 'pratos' ? t('menu.pratosNote') : block.note}
        </SectionNote>
      )}
    </SectionComponent>
  );
};

const PrintableMenu = () => {
  const { t } = useTranslation();
  const { getTranslations } = useMultiLanguage();

  return (
    <Wrapper>
      <PrintStyles />
      <Header className="no-print">
        <Logo src={logo} alt="Cuore Brasiliano" />
        <Title>{t('menu.fullMenuTitle', { defaultValue: 'Menu Completo' })}</Title>
        <PrintButton onClick={() => window.print()}>{t('menu.print', { defaultValue: 'Imprimir' })}</PrintButton>
      </Header>

      {/* Appetizer/Dinner blocks */}
      <Block t={t} keyName="petiscos" getTranslations={getTranslations} />

      {/* Lunch blocks */}
      <Block t={t} keyName="pratos" getTranslations={getTranslations} />
      
      <Block t={t} keyName="bebidas" getTranslations={getTranslations} />
      
      <Block t={t} keyName="drinks" getTranslations={getTranslations} />
      
      <Block t={t} keyName="cervejas" getTranslations={getTranslations} />
      
      <Block t={t} keyName="sobremesas" getTranslations={getTranslations} />
    </Wrapper>
  );
};

export default PrintableMenu;


